import React, {
    forwardRef,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
} from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {
    Box,
    Typography,
    Paper,
    TextField,
    Button,
    Snackbar,
    Alert,
    CircularProgress,
    useTheme,
} from '@mui/material';
import { Send } from '@mui/icons-material';
import { DynamicBreadcrumbs } from 'components/Breadcrumb';
import { apiClient } from 'utils/ApiClient';
import { Logger } from 'utils/Logger';

const EMPTY_QUILL = '<p><br></p>';

const FIND_DOM_NODE_DEPRECATED = 'findDOMNode is deprecated';

/**
 * react-quill calls findDOMNode in componentDidMount; React logs that during the layout phase.
 * A console patch in useEffect runs too late. Install synchronously before this render commits
 * so the child ReactQuill mount sees the filter. React passes the message as the first string
 * argument prefixed with "Warning: ".
 */
let findDomNodeConsoleFilterInstalled = false;
function ensureFindDomNodeConsoleFilter(): void {
    if (findDomNodeConsoleFilterInstalled || typeof window === 'undefined') return;
    findDomNodeConsoleFilterInstalled = true;
    const previous = console.error;
    console.error = (...args: unknown[]) => {
        const suppress = args.some(
            (a) => typeof a === 'string' && a.includes(FIND_DOM_NODE_DEPRECATED),
        );
        if (suppress) return;
        previous.apply(console, args as []);
    };
}

/** Ref-forwarding wrapper; works with deferred editor mount below (Strict Mode). */
const QuillWrapper = forwardRef<ReactQuill, React.ComponentProps<typeof ReactQuill>>((props, ref) => {
    const quillRef = useRef<ReactQuill>(null);
    useImperativeHandle(ref, () => quillRef.current as ReactQuill);
    return <ReactQuill ref={quillRef} {...props} />;
});

QuillWrapper.displayName = 'QuillWrapper';

function bodyHasText(html: string): boolean {
    const plain = html
        .replace(/<[^>]*>/g, ' ')
        .replace(/&nbsp;/gi, ' ')
        .replace(/\s+/g, ' ')
        .trim();
    return plain.length > 0;
}

export const SupportEmail: React.FC = () => {
    ensureFindDomNodeConsoleFilter();

    const theme = useTheme();
    const isLightToolbar = theme.palette.mode === 'light';
    /** DEFAULT + PROFESSIONAL light: secondary gold remaps to low-contrast on paper. */
    const toolbarAccentColor = isLightToolbar ? 'rgb(25, 25, 25)' : theme.palette.secondary.main;
    const toolbarAccentHover = isLightToolbar ? 'rgb(25, 25, 25)' : theme.palette.secondary.dark;

    const [editorReady, setEditorReady] = useState(false);
    useEffect(() => {
        setEditorReady(true);
        return () => setEditorReady(false);
    }, []);

    const quillModules = useMemo(
        () => ({
            toolbar: [
                [{ header: [1, 2, 3, false] }],
                ['bold', 'italic', 'underline', 'strike'],
                [{ list: 'ordered' }, { list: 'bullet' }],
                [{ indent: '-1' }, { indent: '+1' }],
                ['link'],
                ['clean'],
            ],
        }),
        [],
    );

    const [subject, setSubject] = useState('');
    const [bodyHtml, setBodyHtml] = useState(EMPTY_QUILL);
    const [sending, setSending] = useState(false);
    const [formError, setFormError] = useState('');
    const [snackbar, setSnackbar] = useState<{
        open: boolean;
        message: string;
        severity: 'success' | 'error';
    }>({ open: false, message: '', severity: 'success' });

    const handleSend = async () => {
        setFormError('');
        const subj = subject.trim();
        if (!subj) {
            setFormError('Subject is required');
            return;
        }
        if (!bodyHasText(bodyHtml)) {
            setFormError('Message body is required');
            return;
        }

        setSending(true);
        try {
            const res = await apiClient.post('/support/email', {
                subject: subj,
                body: bodyHtml.trim(),
            });
            const msg =
                (res as any)?.data?.message ||
                'Your email has been sent successfully.';
            setSnackbar({ open: true, message: msg, severity: 'success' });
            setSubject('');
            setBodyHtml(EMPTY_QUILL);
        } catch (err: any) {
            Logger.error('SupportEmail', `Send failed: ${err}`);
            const message = err?.message || 'Failed to send email. Please try again.';
            setSnackbar({ open: true, message, severity: 'error' });
        } finally {
            setSending(false);
        }
    };

    return (
        <Box pl={{ xs: 1.25, md: 2.5 }} pt={1} width="100%" mb={3}>
            <DynamicBreadcrumbs
                items={[
                    { itemPath: '/dashboard', itemLabel: 'Home' },
                    { itemPath: '/support/email', itemLabel: 'Email' },
                ]}
            />

            <Paper
                elevation={0}
                sx={{
                    p: 3,
                    mt: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    maxWidth: 900,
                }}
            >
                <Typography variant="h5" fontWeight={700} gutterBottom>
                    Email support
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Describe your request below. Your message is sent to the platform support team.
                </Typography>

                <TextField
                    label="Subject"
                    fullWidth
                    required
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    disabled={sending}
                    sx={{ mb: 2 }}
                />

                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Body
                </Typography>
                <Box
                    sx={{
                        '& .quill': { bgcolor: 'background.paper' },
                        '& .ql-container': {
                            minHeight: 220,
                            borderBottomLeftRadius: 4,
                            borderBottomRightRadius: 4,
                            fontSize: '1rem',
                        },
                        '& .ql-toolbar.ql-snow': {
                            borderTopLeftRadius: 4,
                            borderTopRightRadius: 4,
                            borderColor: 'divider',
                            '& .ql-stroke': {
                                stroke: toolbarAccentColor,
                            },
                            '& .ql-fill': {
                                fill: toolbarAccentColor,
                            },
                            '& .ql-picker': {
                                color: toolbarAccentColor,
                            },
                            '& .ql-picker-label': {
                                color: toolbarAccentColor,
                            },
                            '& .ql-picker-label svg .ql-stroke': {
                                stroke: toolbarAccentColor,
                            },
                            '& .ql-picker-label svg .ql-fill': {
                                fill: toolbarAccentColor,
                            },
                            '& .ql-picker-options': {
                                bgcolor: 'background.paper',
                            },
                            '& .ql-picker-item': {
                                color: toolbarAccentColor,
                            },
                            '& button:hover .ql-stroke': {
                                stroke: toolbarAccentHover,
                            },
                            '& button:hover .ql-fill': {
                                fill: toolbarAccentHover,
                            },
                            '& button.ql-active .ql-stroke': {
                                stroke: toolbarAccentHover,
                            },
                            '& button.ql-active .ql-fill': {
                                fill: toolbarAccentHover,
                            },
                        },
                        '& .ql-container.ql-snow': { borderColor: 'divider' },
                        mb: 2,
                    }}
                >
                    {editorReady ? (
                        <QuillWrapper
                            theme="snow"
                            value={bodyHtml}
                            onChange={setBodyHtml}
                            modules={quillModules}
                            readOnly={sending}
                        />
                    ) : (
                        <Box
                            sx={{
                                minHeight: 268,
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 1,
                                bgcolor: 'action.hover',
                            }}
                        />
                    )}
                </Box>

                {formError ? (
                    <Typography color="error" variant="body2" sx={{ mb: 1 }}>
                        {formError}
                    </Typography>
                ) : null}

                <Button
                    variant="outlined"
                    color="secondary"
                    startIcon={sending ? <CircularProgress size={18} color="inherit" /> : <Send />}
                    onClick={handleSend}
                    disabled={sending}
                >
                    Send
                </Button>
            </Paper>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    severity={snackbar.severity}
                    onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default SupportEmail;
